/**
 * Advanced Cache Optimizer
 * Implements client-side caching strategies and resource optimization
 */
(function() {
  'use strict';

  // Cache configuration
  const cacheConfig = {
    // Static resources cache duration (in milliseconds)
    staticResources: {
      css: 7 * 24 * 60 * 60 * 1000, // 7 days
      js: 7 * 24 * 60 * 60 * 1000,  // 7 days
      images: 30 * 24 * 60 * 60 * 1000, // 30 days
      fonts: 365 * 24 * 60 * 60 * 1000  // 1 year
    },
    // API cache duration
    api: 5 * 60 * 1000, // 5 minutes
    // Page cache duration
    pages: 10 * 60 * 1000 // 10 minutes
  };

  // Service Worker for advanced caching (if supported)
  function registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      // Create service worker content dynamically
      const swContent = `
        const CACHE_NAME = 'mrpork-v1';
        const STATIC_CACHE = 'mrpork-static-v1';
        const IMAGE_CACHE = 'mrpork-images-v1';
        
        // Resources to cache immediately
        const CORE_RESOURCES = [
          '${window.location.origin}/cdn/shop/t/15/assets/base.css',
          '${window.location.origin}/cdn/shop/t/15/assets/global.js',
          '${window.location.origin}/cdn/shop/t/15/assets/performance-optimizations.js'
        ];
        
        // Install event
        self.addEventListener('install', (event) => {
          event.waitUntil(
            caches.open(STATIC_CACHE)
              .then((cache) => cache.addAll(CORE_RESOURCES))
              .then(() => self.skipWaiting())
          );
        });
        
        // Activate event
        self.addEventListener('activate', (event) => {
          event.waitUntil(
            caches.keys().then((cacheNames) => {
              return Promise.all(
                cacheNames.map((cacheName) => {
                  if (cacheName !== CACHE_NAME && 
                      cacheName !== STATIC_CACHE && 
                      cacheName !== IMAGE_CACHE) {
                    return caches.delete(cacheName);
                  }
                })
              );
            }).then(() => self.clients.claim())
          );
        });
        
        // Fetch event with cache strategies
        self.addEventListener('fetch', (event) => {
          const url = new URL(event.request.url);
          
          // Cache images with cache-first strategy
          if (url.pathname.match(/\\.(jpg|jpeg|png|gif|webp|avif|svg)$/i)) {
            event.respondWith(
              caches.open(IMAGE_CACHE).then((cache) => {
                return cache.match(event.request).then((response) => {
                  if (response) {
                    return response;
                  }
                  return fetch(event.request).then((fetchResponse) => {
                    cache.put(event.request, fetchResponse.clone());
                    return fetchResponse;
                  });
                });
              })
            );
            return;
          }
          
          // Cache CSS/JS with stale-while-revalidate
          if (url.pathname.match(/\\.(css|js)$/i)) {
            event.respondWith(
              caches.open(STATIC_CACHE).then((cache) => {
                return cache.match(event.request).then((response) => {
                  const fetchPromise = fetch(event.request).then((fetchResponse) => {
                    cache.put(event.request, fetchResponse.clone());
                    return fetchResponse;
                  });
                  return response || fetchPromise;
                });
              })
            );
            return;
          }
        });
      `;
      
      // Register service worker
      const blob = new Blob([swContent], { type: 'application/javascript' });
      const swUrl = URL.createObjectURL(blob);
      
      navigator.serviceWorker.register(swUrl)
        .then((registration) => {
          console.log('Service Worker registered successfully');
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    }
  }

  // Local Storage cache for API responses
  class LocalStorageCache {
    static set(key, data, duration = cacheConfig.api) {
      const expiry = Date.now() + duration;
      const cacheData = {
        data: data,
        expiry: expiry
      };
      
      try {
        localStorage.setItem(`cache_${key}`, JSON.stringify(cacheData));
      } catch (e) {
        // Storage might be full, clear old entries
        this.cleanup();
      }
    }
    
    static get(key) {
      try {
        const cached = localStorage.getItem(`cache_${key}`);
        if (!cached) return null;
        
        const cacheData = JSON.parse(cached);
        if (Date.now() > cacheData.expiry) {
          localStorage.removeItem(`cache_${key}`);
          return null;
        }
        
        return cacheData.data;
      } catch (e) {
        return null;
      }
    }
    
    static cleanup() {
      // Remove expired entries
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cache_')) {
          const cached = localStorage.getItem(key);
          try {
            const cacheData = JSON.parse(cached);
            if (Date.now() > cacheData.expiry) {
              localStorage.removeItem(key);
            }
          } catch (e) {
            localStorage.removeItem(key);
          }
        }
      }
    }
  }

  // Memory cache for frequently accessed data
  class MemoryCache {
    constructor() {
      this.cache = new Map();
      this.timers = new Map();
    }
    
    set(key, data, duration = cacheConfig.api) {
      // Clear existing timer
      if (this.timers.has(key)) {
        clearTimeout(this.timers.get(key));
      }
      
      this.cache.set(key, data);
      
      // Set expiration timer
      const timer = setTimeout(() => {
        this.cache.delete(key);
        this.timers.delete(key);
      }, duration);
      
      this.timers.set(key, timer);
    }
    
    get(key) {
      return this.cache.get(key);
    }
    
    has(key) {
      return this.cache.has(key);
    }
    
    clear() {
      this.timers.forEach(timer => clearTimeout(timer));
      this.cache.clear();
      this.timers.clear();
    }
  }

  // Global memory cache instance
  const memoryCache = new MemoryCache();

  // Optimize fetch requests with caching
  function optimizeFetch() {
    const originalFetch = window.fetch;
    
    window.fetch = async function(input, init = {}) {
      const url = typeof input === 'string' ? input : input.url;
      const method = init.method || 'GET';
      
      // Only cache GET requests
      if (method.toUpperCase() !== 'GET') {
        return originalFetch.call(this, input, init);
      }
      
      // Create cache key
      const cacheKey = `fetch_${url}`;
      
      // Check memory cache first
      if (memoryCache.has(cacheKey)) {
        const cachedResponse = memoryCache.get(cacheKey);
        return Promise.resolve(new Response(JSON.stringify(cachedResponse.data), {
          status: cachedResponse.status,
          statusText: cachedResponse.statusText,
          headers: cachedResponse.headers
        }));
      }
      
      // Check localStorage cache
      const cachedData = LocalStorageCache.get(cacheKey);
      if (cachedData) {
        memoryCache.set(cacheKey, cachedData);
        return Promise.resolve(new Response(JSON.stringify(cachedData.data), {
          status: cachedData.status,
          statusText: cachedData.statusText,
          headers: cachedData.headers
        }));
      }
      
      // Make actual request
      const response = await originalFetch.call(this, input, init);
      
      // Cache successful responses
      if (response.ok && response.status < 300) {
        try {
          const clonedResponse = response.clone();
          const data = await clonedResponse.json();
          
          const cacheData = {
            data: data,
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries())
          };
          
          // Cache in memory and localStorage
          memoryCache.set(cacheKey, cacheData);
          LocalStorageCache.set(cacheKey, cacheData);
          
        } catch (e) {
          // Response might not be JSON, skip caching
        }
      }
      
      return response;
    };
  }

  // Resource preloading with cache optimization
  function preloadResources() {
    // Preload critical CSS
    const criticalCSS = [
      'component-card.css',
      'component-price.css',
      'base.css'
    ];
    
    criticalCSS.forEach(cssFile => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = `/cdn/shop/t/15/assets/${cssFile}`;
      document.head.appendChild(link);
    });
    
    // Preload critical JS
    const criticalJS = [
      'global.js',
      'performance-optimizations.js'
    ];
    
    criticalJS.forEach(jsFile => {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = `/cdn/shop/t/15/assets/${jsFile}`;
      document.head.appendChild(link);
    });
  }

  // HTTP/2 Server Push simulation with preload hints
  function addPreloadHints() {
    const resources = [
      { href: '/cdn/shop/t/15/assets/base.css', as: 'style' },
      { href: '/cdn/shop/t/15/assets/global.js', as: 'script' },
      { href: '/cdn/shopifycloud/shopify/assets/web-pixels-manager-setup-*.js', as: 'script' }
    ];
    
    resources.forEach(resource => {
      const existing = document.querySelector(`link[href*="${resource.href}"]`);
      if (!existing) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        if (resource.as === 'style') {
          link.onload = function() { this.rel = 'stylesheet'; };
        }
        document.head.appendChild(link);
      }
    });
  }

  // Browser cache optimization
  function optimizeBrowserCache() {
    // Add cache headers via meta tags (for reference)
    const cacheHeaders = [
      { name: 'Cache-Control', content: 'public, max-age=31536000' }, // Static resources
      { name: 'ETag', content: `"${Date.now()}"` } // Entity tag
    ];
    
    // Note: These are informational only, actual cache headers must be set server-side
    console.log('Recommended cache headers:', cacheHeaders);
  }

  // Performance monitoring for cache effectiveness
  function monitorCachePerformance() {
    if ('performance' in window && 'getEntriesByType' in performance) {
      setTimeout(() => {
        const resources = performance.getEntriesByType('resource');
        const cacheHits = resources.filter(r => r.transferSize === 0).length;
        const totalResources = resources.length;
        
        console.log(`Cache efficiency: ${Math.round((cacheHits/totalResources) * 100)}%`);
        console.log(`Cache hits: ${cacheHits}/${totalResources} resources`);
      }, 3000);
    }
  }

  // Main initialization
  function init() {
    // Register service worker for advanced caching
    registerServiceWorker();
    
    // Optimize fetch with caching
    optimizeFetch();
    
    // Preload critical resources
    preloadResources();
    
    // Add preload hints
    addPreloadHints();
    
    // Browser cache optimization
    optimizeBrowserCache();
    
    // Monitor cache performance
    monitorCachePerformance();
    
    // Clean up old cache entries periodically
    setInterval(() => {
      LocalStorageCache.cleanup();
    }, 60000); // Every minute
    
    console.log('Advanced cache optimizations applied');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();